/* Search Modal behavior for Liquid theme
   - Opens / closes modal
   - Debounced search using Shopify suggest endpoint
   - Renders results with image, type and price
   - Exposes window.openSearchModal() for header button
*/
(function () {
  function $(sel, ctx = document) {
    return ctx.querySelector(sel);
  }

  const modal = document.getElementById('search-modal');
  if (!modal) return;

  const input = $("#search-input", modal);
  const closeBtn = $("#search-close", modal);
  const resultsRoot = $("#search-results", modal);
  const stateBox = $("#search-state", modal);
  const stateTitle = $("#search-state-title", modal);
  const stateSub = $("#search-state-sub", modal);
  const currency = modal.dataset.currency || 'USD';

  let debounceTimer = null;
  let ignoreOutsideClick = false;

  function openSearch() {
    modal.classList.remove('hidden');
    document.documentElement.style.overflow = 'hidden';
    // Prevent the same opening click from immediately closing the modal
    ignoreOutsideClick = true;
    setTimeout(() => { ignoreOutsideClick = false; }, 0);
    setTimeout(() => input && input.focus(), 100);
  }

  function closeSearch() {
    modal.classList.add('hidden');
    document.documentElement.style.overflow = '';
    if (input) input.value = '';
    renderStart();
  }

  // Expose for manual invocation (e.g., header button onclick="openSearchModal()")
  window.openSearchModal = openSearch;
  window.closeSearchModal = closeSearch;

  function clearResults() {
    const items = resultsRoot.querySelectorAll('.search-item');
    items.forEach((n) => n.remove());
  }

  function renderStart() {
    clearResults();
    stateTitle.textContent = 'Start searching';
    stateSub.textContent = 'Search for products by name, type, or category.';
    stateBox.style.display = 'flex';
  }

  function renderLoading() {
    clearResults();
    stateTitle.textContent = 'Loading...';
    stateSub.textContent = '';
    stateBox.style.display = 'flex';
  }

  function renderError(msg) {
    clearResults();
    stateTitle.textContent = 'Error loading products';
    stateSub.textContent = msg || 'Please try refreshing the page.';
    stateBox.style.display = 'flex';
  }

  function renderNoResults() {
    clearResults();
    stateTitle.textContent = 'No results found';
    stateSub.textContent = 'Try searching with different keywords or browse our categories.';
    stateBox.style.display = 'flex';
  }

  function formatCurrency(amount) {
    try {
      const value = Number(amount);
      if (isNaN(value)) return '';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(value);
    } catch (e) {
      return amount;
    }
  }

  function renderResults(products) {
    clearResults();
    stateBox.style.display = 'none';

    const list = document.createElement('div');
    list.className = 'p-4 sm:p-6 space-y-3';

    products.forEach((p) => {
      const url = p.url || (p.handle ? `/products/${p.handle}` : '#');
      const title = p.title || p.product_title || 'Product';
      const productType = p.product_type || p.type || p.productType || '';
      let img = '';
      if (p.image) {
        if (typeof p.image === 'string') img = p.image;
        else if (p.image.src) img = p.image.src;
      }
      img = img || '/assets/placeholder.jpg';

      // Price extraction - support a few possible shapes
      let price = '';
      if (p.price) price = p.price;
      else if (p.price_min) price = p.price_min;
      else if (p.price_range) price = p.price_range.min || '';
      else if (p.variants && p.variants[0] && p.variants[0].price) price = p.variants[0].price;

      const item = document.createElement('a');
      item.href = url;
      item.className = 'search-item flex items-center gap-4 p-3 hover:bg-gray-50 rounded-lg transition-colors';
      item.addEventListener('click', () => closeSearch());

      const imgWrap = document.createElement('div');
      imgWrap.className = 'flex-shrink-0';
      const imgEl = document.createElement('img');
      imgEl.src = img;
      imgEl.alt = title;
      imgEl.className = 'w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-lg';
      imgWrap.appendChild(imgEl);

      const details = document.createElement('div');
      details.className = 'search-item__info';

      // Title (use h3 for accessibility / consistency with theme JS)
      const titleEl = document.createElement('h3');
      titleEl.className = 'search-item__title';
      titleEl.textContent = title;
      // Inline sizing to ensure theme JS-rendered titles match modal design
      titleEl.style.fontSize = '13px';
      titleEl.style.lineHeight = '1.2';
      details.appendChild(titleEl);

      // Price
      if (price) {
        const pPrice = document.createElement('p');
        pPrice.className = 'search-item__price';
        // Normalize numeric price if necessary
        let numeric = price;
        if (typeof price === 'string' && price.indexOf('.') === -1 && price.length > 3) {
          numeric = (parseFloat(price) / 100).toFixed(2);
        }
        pPrice.textContent = formatCurrency(numeric);
        details.appendChild(pPrice);
      } else if (productType) {
        // Fallback: show product type if price not available
        const pType = document.createElement('p');
        pType.className = 'search-item__type';
        pType.textContent = productType;
        details.appendChild(pType);
      }

      item.appendChild(imgWrap);
      item.appendChild(details);
      list.appendChild(item);
    });

    resultsRoot.appendChild(list);
  }

  // Debounced input handler
  if (input) {
    input.addEventListener('input', function () {
      const q = input.value.trim();
      if (!q) {
        renderStart();
        return;
      }
      renderLoading();
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => performSearch(q), 300);
    });
  }

  async function performSearch(query) {
    try {
      // First try: custom products API (Next.js) which can return more comprehensive results
      try {
        const apiResp = await fetch('/api/shopify-products?q=' + encodeURIComponent(query) + '&limit=200');
        if (apiResp.ok) {
          const apiData = await apiResp.json();
          const apiProducts = apiData && apiData.products ? apiData.products : apiData || [];
          if (Array.isArray(apiProducts) && apiProducts.length > 0) {
            renderResults(apiProducts);
            return;
          }
        }
      } catch (e) {
        // ignore and fall back to suggest endpoint
        console.debug('shopify-products API not available, falling back to suggest', e);
      }

      // Fallback: Use Shopify suggest endpoint which is available in themes
      const resp = await fetch('/search/suggest.json?resources[type]=product&limit=50&q=' + encodeURIComponent(query));
      if (!resp.ok) throw new Error('Request failed');
      const data = await resp.json();
      const products = (data.resources && data.resources.results && data.resources.results.products) || data.results || data.products || [];
      if (!products || products.length === 0) {
        renderNoResults();
        return;
      }
      renderResults(products);
    } catch (err) {
      console.error('Search error', err);
      renderError(err && err.message ? err.message : 'Failed to fetch products');
    }
  }

  // Close handlers
  if (closeBtn) closeBtn.addEventListener('click', closeSearch);
  modal.addEventListener('click', function (e) {
    if (e.target === modal) closeSearch();
  });
  // Close when clicking anywhere outside the modal content
  document.addEventListener('click', function (e) {
    if (modal.classList.contains('hidden') || ignoreOutsideClick) return;
    const content = modal.querySelector('.search-modal__content');
    if (!content) return;
    if (!content.contains(e.target)) {
      closeSearch();
    }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeSearch();
  });

  // Initialize start state
  renderStart();

})();
